import React from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { RoutesEnum } from "~/types/enums";
import Button from "~/components-shared/Button";
import UserDropdown from "~/components/UserDropdown";
import Icon from "~/components-shared/Icon";
import { Badge } from "~/components-shared";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import useAbacExt from "~/hooks/abac";
import Branding from "~/components/Branding";
import mixpanel from "mixpanel-browser";
import { menuItems } from "~/lib/menu";
const Wrapper = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 18.6rem;
  min-width: 18.6rem;
  max-width: 18.6rem;
  position: fixed;
  left: 50%;
  margin-left: -51.4rem;
  top: 0;
  z-index: 10;
  border-right: 1px solid ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.borders.primary;
}};
  background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.secondary;
}};
  padding-top: 3.5rem;

  @media (max-width: 1440px) {
    left: 0;
    margin-left: 0;
  }
`;
const StyledBadge = styled(Badge)`
  float: right;
`;
const SectionList = styled.ul`
  margin: 0;
  list-style: none;
  overflow: auto;
  width: 100%;
  flex-grow: 1;
  transition: all 300ms ease-out;
  padding: 0 1.5rem;

  &::-webkit-scrollbar {
    display: none;
  }
  &::-webkit-scrollbar-thumb {
    display: none;
  }
  &:hover::-webkit-scrollbar-thumb {
    display: initial;
  }
`;
const SectionText = styled.span`
  display: inline-block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  padding: 0 1rem;
  color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.text.secondary;
}};
`;
const List = styled.ul`
  padding: 0;
  margin: 0 0 1rem;
  list-style: none;
  width: 100%;
`;
const SubList = styled.ul`
  padding: 0;
  margin: 0 0 0 1rem;
  list-style: none;
  width: calc(100% - 1rem);
`;
const ListItem = styled.li`
  margin: 0;
`;
const SectionListItem = styled.li`
  margin: 0;
  padding: 0;
`;
const UserWrapper = styled.div`
  padding: 0 1.5rem 1.5rem;
`;
const LeftSideBar = () => {
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const {
    pathname
  } = useLocation();
  const {
    userHasOneOf
  } = useAbacExt();
  const menu = menuItems;
  const handleClickMenuItem = (name, route) => {
    mixpanel.track(`Menu Click – ${name}`);
    if (route === pathname) {
      return;
    }
    if (Object.values(RoutesEnum).includes(route)) {
      history.push(route);
    } else {
      window.open(route, "_blank");
    }
  };
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Branding, {
    s: {
      px: 7,
      pb: 5
    }
  }), /*#__PURE__*/React.createElement(SectionList, null, Object.values(menu).map(menuSection => Object.values(menuSection.items).some(item => !item.permissions.length ? true : userHasOneOf(item.permissions)) && /*#__PURE__*/React.createElement(SectionListItem, {
    key: menuSection.id
  }, menuSection.name && /*#__PURE__*/React.createElement(SectionText, null, menuSection.name), /*#__PURE__*/React.createElement(List, null, Object.values(menuSection.items).map(menuItem => (menuItem.permissions.length && userHasOneOf(menuItem.permissions) || !menuItem.permissions.length) && /*#__PURE__*/React.createElement(ListItem, {
    key: menuItem.id
  }, /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: menuItem.icon,
      color: colors.secondary.main,
      scale: 5,
      s: {
        mr: 2
      }
    }),
    color: menuItem.matches.some(v => pathname.includes(v)) ? "translucentSecondary" : "transparent",
    onClick: () => handleClickMenuItem(menuItem.name, menuItem.route),
    s: {
      mb: 1,
      pr: 3
    },
    textAlign: "left",
    fullWidth: true,
    disabled: !!menuItem.comingSoon,
    outline: false
  }, menuItem.name, menuItem.comingSoon && /*#__PURE__*/React.createElement(StyledBadge, {
    fontSize: 2.5,
    color: "secondary"
  }, t("LeftSideBar.comingSoon")), menuItem.beta && /*#__PURE__*/React.createElement(StyledBadge, {
    fontSize: 2.5,
    s: {
      py: 0.3,
      ml: 1,
      mt: -0.3
    },
    color: "translucentSuccess"
  }, t("LeftSideBar.beta"))), menuItem.matches.some(v => pathname.includes(v)) && menuItem.items && /*#__PURE__*/React.createElement(SubList, null, Object.values(menuItem.items).map(menuSubitem => (menuSubitem.permissions.length && userHasOneOf(menuSubitem.permissions) || !menuSubitem.permissions.length) && /*#__PURE__*/React.createElement(ListItem, {
    key: menuSubitem.id
  }, /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: menuSubitem.icon,
      color: colors.secondary.main,
      scale: 5,
      s: {
        mr: 2
      }
    }),
    color: menuSubitem.matches.some(v => pathname.includes(v)) ? "translucentSecondary" : "transparent",
    onClick: () => handleClickMenuItem(menuSubitem.name, menuSubitem.route),
    s: {
      mb: 1
    },
    textAlign: "left",
    fullWidth: true,
    disabled: !!menuSubitem.comingSoon,
    outline: false
  }, menuSubitem.name, menuSubitem.comingSoon && /*#__PURE__*/React.createElement(StyledBadge, {
    fontSize: 2.5,
    color: "secondary"
  }, t("LeftSideBar.comingSoon")), menuSubitem.beta && /*#__PURE__*/React.createElement(StyledBadge, {
    fontSize: 2.5,
    s: {
      py: 0.3,
      ml: 1,
      mt: -0.3
    },
    color: "translucentSuccess"
  }, t("LeftSideBar.beta")))))))))))), /*#__PURE__*/React.createElement(UserWrapper, null, /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(UserDropdown, {
    direction: "topRight"
  }))));
};
export default LeftSideBar;